/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Forecast accuracy in plain
 * terms. For each of the last six closed months: what the model expected a
 * month ahead, what happened, the difference, an accuracy percentage, and what
 * simply repeating the previous month would have scored. The summary weighs
 * the months by their volume, so one big month counts for more than a small
 * one. The details page section and the chat explanation read the same
 * numbers, so they always agree.
 */
import { sum } from './forecast-math';
import type { AccuracyRow, MonthAxis, MonthKey } from './forecast-types';

export interface AccuracyMonth {
  month: MonthKey;
  /** What the model expected for the month, set the month before. */
  expected: number;
  /** Recorded sales and usage, before any buyer edits. */
  actual: number;
  /** Expected minus actual: above zero the forecast ran high, below it ran low. */
  difference: number;
  /** 100 minus the miss as a share of what happened, 0 to 100; null with no demand. */
  accuracyPercent: number | null;
  /** The previous month's actual, which repeating last month would have used. */
  repeated: number | null;
  /** What repeating the previous month would have scored, 0 to 100. */
  repeatAccuracyPercent: number | null;
}

export type Lean = 'high' | 'low' | 'level';

export interface AccuracySummary {
  months: AccuracyMonth[];
  /** 100 minus the total miss as a share of total actual, 0 to 100; null with no demand. */
  accuracyPercent: number | null;
  /** Net expected minus actual as a share of total actual: +3 reads as 3% high. */
  leanPercent: number | null;
  lean: Lean;
  /** Months the forecast ran above what happened. */
  highMonths: number;
  /** Months it ran below. */
  lowMonths: number;
  /** What simply repeating the previous month would have scored, 0 to 100. */
  repeatAccuracyPercent: number | null;
}

/** The slice of an engine result the summary needs. */
export interface AccuracyInput {
  accuracy: readonly AccuracyRow[];
  /** Recorded actuals for every history month, oldest first. */
  raw: readonly number[];
  axis: MonthAxis;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function accuracyOf(miss: number, actual: number): number | null {
  return actual > 0 ? clampPercent(100 - (miss / actual) * 100) : null;
}

export function summarizeAccuracy(input: AccuracyInput): AccuracySummary {
  const months = input.accuracy.map((row): AccuracyMonth => {
    const index = input.axis.history.findIndex(
      (month) => month.key === row.month.key,
    );
    const repeated = index > 0 ? (input.raw[index - 1] ?? null) : null;
    const difference = row.forecast - row.actual;
    return {
      month: row.month,
      expected: row.forecast,
      actual: row.actual,
      difference,
      accuracyPercent: accuracyOf(Math.abs(difference), row.actual),
      repeated,
      repeatAccuracyPercent:
        repeated === null
          ? null
          : accuracyOf(Math.abs(repeated - row.actual), row.actual),
    };
  });
  const totalActual = sum(months.map((month) => month.actual));
  const totalMiss = sum(months.map((month) => Math.abs(month.difference)));
  const net = sum(months.map((month) => month.difference));
  const comparable = months.filter((month) => month.repeated !== null);
  const repeatActual = sum(comparable.map((month) => month.actual));
  const repeatMiss = sum(
    comparable.map((month) => Math.abs((month.repeated ?? 0) - month.actual)),
  );
  const leanPercent = totalActual > 0 ? (net / totalActual) * 100 : null;
  const lean: Lean =
    leanPercent === null || Math.round(leanPercent) === 0
      ? 'level'
      : leanPercent > 0
        ? 'high'
        : 'low';
  return {
    months,
    accuracyPercent: accuracyOf(totalMiss, totalActual),
    leanPercent,
    lean,
    highMonths: months.filter((month) => month.difference > 0).length,
    lowMonths: months.filter((month) => month.difference < 0).length,
    repeatAccuracyPercent: accuracyOf(repeatMiss, repeatActual),
  };
}

/** "91%": whole percents, the way the page and the chat say them. */
export function fmtPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** "leaning 3% high (over in 4 of 6 months)", or "with no lean either way". */
export function leanText(summary: AccuracySummary): string {
  const { lean, leanPercent, highMonths, lowMonths, months } = summary;
  if (lean === 'level' || leanPercent === null) {
    return 'with no lean either way';
  }
  const count = lean === 'high' ? highMonths : lowMonths;
  const direction = lean === 'high' ? 'over' : 'under';
  return `leaning ${fmtPercent(Math.abs(leanPercent))} ${lean} (${direction} in ${count} of ${months.length} months)`;
}

/** The chat explanation's line on accuracy; null when there is nothing to score. */
export function accuracySentence(summary: AccuracySummary): string | null {
  if (summary.accuracyPercent === null) return null;
  const repeat =
    summary.repeatAccuracyPercent === null
      ? ''
      : ` Simply repeating the previous month would have scored ${fmtPercent(summary.repeatAccuracyPercent)}.`;
  return `Forecast accuracy: ${fmtPercent(summary.accuracyPercent)} over the last ${summary.months.length} months, ${leanText(summary)}.${repeat}`;
}
